import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';
import './Dashboard.css';
import VendorCards from '../Vendor/VendorCards';
import VendorDetailsPage from '../VendorDetails/VendorDetailsPage';
import VendorOverviewPage from '../VendorDetails/VendorOverviewPage';
import FrameworksPage from '../Compliance/Frameworks';
import RequirementsPage from '../Compliance/RequirementsPage';
import Tests from '../Tests/Tests';
import DocumentsPage from '../Compliance/Documents';
import ControlsPage from '../Compliance/Controls';
import DocumentDetails from '../Compliance/DocumentsDetails';
import Assets from '../Assets/Assets';
import PoliciesPage from '../Compliance/PoliciesPage';
import AuditPage from '../Compliance/AuditPage';
import TestDetails from '../Tests/TestDetails';
import Settings from '../Settings/Settings';
import OkfContextExportSettings from '../Settings/OkfContextExportSettings';
import MfaReminderBanner from '../Settings/MfaReminderBanner';
import RaiseTicketModal from '../Support/RaiseTicketModal';
import MyTickets from '../Support/MyTickets';
import UserHome from '../UserHome/UserHome';
import Vendors from '../Vendors/Vendors';
import ScopeBoundaryPage from '../Scope/ScopeBoundaryPage';
import EvidencePage from '../Evidence/EvidencePage';
import OrgDocumentEditor from '../OrgDocuments/OrgDocumentEditor';
import ResellerDashboard from '../Marketplace/ResellerDashboard';
import CpaDashboard from '../Marketplace/CpaDashboard';
import BillingPage from '../Marketplace/BillingPage';
import AsmDashboard from '../Admin/AsmDashboard';
import InternalAsmDashboard from '../Admin/iasm/InternalAsmDashboard';
import OrgRedTeamPlanning from '../RedTeam/OrgRedTeamPlanning';
import ComplianceHub from '../Compliance/ComplianceHub';
import ExecutiveReports from './ExecutiveReports';
import MarketplaceDirectory from '../Marketplace/MarketplaceDirectory';
import PartnerMarketplace from '../Marketplace/PartnerMarketplace';
import MyClients from '../Partner/MyClients';
import ClientView from '../Partner/ClientView';
import CpaFindingsInbox from '../Partner/CpaFindingsInbox';
import PartnerBilling from '../Partner/PartnerBilling';
import PartnerLeads from '../Partner/PartnerLeads';
import PartnerBrandingSettings from '../Partner/PartnerBrandingSettings';
import PartnerStripeConnect from '../Partner/PartnerStripeConnect';
import PartnerRedTeam from '../Partner/PartnerRedTeam';
import PeoplePage from '../People/PeoplePage';
import TrustCenterSettings from '../TrustCenter/TrustCenterSettings';
import AccessReviewPage from '../Compliance/AccessReviewPage';
import AuditHub from '../Compliance/AuditHub';
import TrainingPage from '../Compliance/TrainingPage';
import MyTasksPage from '../Compliance/MyTasksPage';
import MetadataLibrary from '../Admin/MetadataLibrary/MetadataLibrary';
import AiGovOverview from '../AiGovernance/AiGovOverview';
import AiAssetInventory from '../AiGovernance/AiAssetInventory';
import StatementOfApplicability from '../AiGovernance/StatementOfApplicability';
import IsmsOverview from '../Isms/IsmsOverview';
import IsmsRoles from '../Isms/IsmsRoles';
import RiskRegister from '../Isms/RiskRegister';
import IsmsSoa from '../Isms/StatementOfApplicability';
import { modulesForFrameworks } from './frameworkModules';
import AiRiskRegister from '../AiGovernance/AiRiskRegister';
import AiGovDocuments from '../AiGovernance/AiGovDocuments';
import AiCcmMonitoring from '../AiGovernance/AiCcmMonitoring';
import AmlOverview from '../Aml/AmlOverview';
import AmlRiskAssessment from '../Aml/AmlRiskAssessment';
import AmlCustomerSegments from '../Aml/AmlCustomerSegments';
import AmlFilings from '../Aml/AmlFilings';
import AmlVendorDueDiligence from '../Aml/AmlVendorDueDiligence';
import { resolveAmlFrameworks, getComplianceSummary } from '../Aml/amlApi';
import { getOrgPermissions } from '../../utils/permissions';
import EngagementsPage from '../Engagements/EngagementsPage';
import EngagementDetail from '../Engagements/EngagementDetail';
import { isTestOrg, clearTestOrg } from '../../utils/testOrg';

// Sidebar IA is organized by USER JOURNEY, not by which engine built each feature.
// Two journeys, both looping back to the audit: (1) get compliant [Compliance] and
// (2) prove it technically [Attack Surface & Testing]. Assets & Vendors is the shared
// source-of-truth feeding both. AI Governance is a nested workspace under Compliance
// (ISO 42001 / NIST AI RMF are just frameworks), not a parallel top-level product.
const NAV_STANDARD = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard/userHome',              icon: 'fa-th-large',             label: 'Dashboard' },
    ],
  },
  {
    label: 'Assets & Vendors',
    amlLabel: 'Testing',
    items: [
      { to: '/dashboard/vendors',               icon: 'fa-store',                label: 'Vendors',        grcOnly: true },
      { to: '/dashboard/assets',                icon: 'fa-server',               label: 'Assets',         grcOnly: true },
      { to: '/dashboard/tests',                 icon: 'fa-flask',                label: 'Tests' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/dashboard/compliance/frameworks', icon: 'fa-sitemap',              label: 'Frameworks' },
      { to: '/dashboard/compliance/hub',        icon: 'fa-project-diagram',      label: 'Compliance Hub', grcOnly: true },
      { to: '/dashboard/compliance/controls',   icon: 'fa-shield-alt',           label: 'Controls' },
      { to: '/dashboard/compliance/policies',   icon: 'fa-file-signature',       label: 'Policies' },
      { to: '/dashboard/compliance/documents',  icon: 'fa-file-alt',             label: 'Documents' },
      { to: '/dashboard/compliance/audits',     icon: 'fa-clipboard-check',      label: 'Audits' },
      { to: '/dashboard/evidence',              icon: 'fa-paperclip',            label: 'Evidence' },
      { to: '/dashboard/ai-governance',         icon: 'fa-brain',                label: 'AI Governance',  grcOnly: true },
      { to: '/dashboard/aml',                   icon: 'fa-university',           label: 'AML Program',    isNew: true, requiresAml: true },
    ],
  },
  {
    label: 'Attack Surface & Testing',
    grcOnly: true,
    items: [
      { to: '/dashboard/scope',                 icon: 'fa-draw-polygon',         label: 'Scope & Boundary' },
      { to: '/dashboard/asm',                   icon: 'fa-globe',                label: 'External ASM' },
      { to: '/dashboard/iasm',                  icon: 'fa-network-wired',        label: 'Internal ASM & BAS' },
      { to: '/dashboard/red-team-planning',     icon: 'fa-skull-crossbones',     label: 'Red Team Planning', requiresOrgManager: true },
      { to: '/dashboard/engagements',           icon: 'fa-file-signature',       label: 'Service Onboarding', isNew: true },
    ],
  },
  {
    label: 'People & Governance',
    items: [
      { to: '/dashboard/people',                icon: 'fa-users',                label: 'People' },
      { to: '/dashboard/training',              icon: 'fa-graduation-cap',       label: 'Training' },
      { to: '/dashboard/access-reviews',        icon: 'fa-user-check',           label: 'Access Reviews' },
      { to: '/dashboard/my-tasks',              icon: 'fa-tasks',                label: 'My Tasks' },
    ],
  },
  {
    label: 'Reporting & Sharing',
    items: [
      { to: '/dashboard/reports',               icon: 'fa-chart-bar',            label: 'Executive Reports' },
      { to: '/dashboard/trust-center',          icon: 'fa-award',                label: 'Trust Center' },
      { to: '/dashboard/audit-hub',             icon: 'fa-user-shield',          label: 'Auditor Portal' },
      { to: '/dashboard/okf',                   icon: 'fa-cloud-upload-alt',     label: 'AI Context Export' },
    ],
  },
];

const NAV_CISO = [
  {
    label: 'Reseller Portal',
    items: [
      { to: '/dashboard/reseller',              icon: 'fa-handshake',            label: 'My Dashboard' },
    ],
  },
  {
    label: 'Attack Surface',
    items: [
      { to: '/dashboard/asm',                   icon: 'fa-globe',                label: 'My Domains' },
      { to: '/dashboard/red-team',              icon: 'fa-skull-crossbones',     label: 'Red Team Planning' },
      { to: '/dashboard/iasm',                  icon: 'fa-network-wired',        label: 'Internal ASM' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/dashboard/compliance/frameworks', icon: 'fa-sitemap',              label: 'Frameworks' },
      { to: '/dashboard/compliance/audits',     icon: 'fa-clipboard-check',      label: 'Audits' },
      { to: '/dashboard/compliance/documents',  icon: 'fa-file-alt',             label: 'Documents' },
    ],
  },
  {
    label: 'Client Management',
    items: [
      { to: '/dashboard/clients',              icon: 'fa-building',             label: 'My Clients' },
      { to: '/dashboard/client-view',          icon: 'fa-eye',                  label: 'Client View' },
    ],
  },
  {
    label: 'Partner Tools',
    items: [
      { to: '/dashboard/reports',              icon: 'fa-chart-bar',            label: 'Executive Reports' },
      { to: '/dashboard/marketplace',          icon: 'fa-store',                label: 'Find a Partner' },
      { to: '/dashboard/partner',              icon: 'fa-id-badge',             label: 'My Listing' },
      { to: '/dashboard/partner-billing',     icon: 'fa-file-invoice-dollar',  label: 'Billing' },
      { to: '/dashboard/partner-stripe',       icon: 'fa-credit-card',          label: 'Stripe' },
      { to: '/dashboard/partner-leads',        icon: 'fa-bolt',                 label: 'Light Scans' },
      { to: '/dashboard/partner-branding',     icon: 'fa-palette',              label: 'Branding' },
    ],
  },
];

const NAV_CPA = [
  {
    label: 'Workspace',
    items: [
      { to: '/dashboard/cpa',            icon: 'fa-balance-scale',   label: 'Command Center' },
      { to: '/dashboard/cpa-findings',   icon: 'fa-inbox',           label: 'Findings Inbox' },
    ],
  },
  {
    label: 'Clients',
    items: [
      { to: '/dashboard/clients',        icon: 'fa-building',        label: 'All Clients' },
    ],
  },
  {
    label: 'Lead Pipeline',
    items: [
      { to: '/dashboard/partner-leads',  icon: 'fa-bolt',            label: 'Light Scans' },
      { to: '/dashboard/partner',        icon: 'fa-id-badge',        label: 'Marketplace Listing' },
    ],
  },
  {
    label: 'Firm Settings',
    items: [
      { to: '/dashboard/partner-branding', icon: 'fa-palette',         label: 'Branding' },
      { to: '/dashboard/partner-stripe',   icon: 'fa-credit-card',     label: 'Stripe Connect' },
      { to: '/dashboard/partner-billing',  icon: 'fa-file-invoice-dollar', label: 'Billing' },
    ],
  },
];

// Meta Library (compliance metadata / policy-template definitions). Surfaced in the CPA portal for
// CPA_ADMIN only — the backend already authorises CPA_ADMIN (ROLE_ADMIN authority); it was previously
// reachable only from the platform-admin portal, which CPA admins can't enter.
const NAV_CPA_LIBRARY = {
  label: 'Library',
  items: [
    { to: '/dashboard/meta-library', icon: 'fa-database', label: 'Meta Library' },
  ],
};

function Dashboard() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed]           = useState(false);
  const [showSupport, setShowSupport]       = useState(false);
  const [clientContextName, setClientContextName] = useState(localStorage.getItem('partnerContextOrgName') || '');
  const [isDemo, setIsDemo]                 = useState(false);
  const navigate    = useNavigate();
  const location    = useLocation();
  const username    = localStorage.getItem('userName') || localStorage.getItem('username') || 'User';
  const org         = localStorage.getItem('organization') || '';
  const orgRole     = localStorage.getItem('orgRole') || '';
  const isCiso      = orgRole === 'CISO_RESELLER';
  const isCpa       = orgRole === 'CPA_AUDITOR' || orgRole === 'CPA_ADMIN';
  const isCpaAdmin  = orgRole === 'CPA_ADMIN';
  const isPartner   = orgRole === 'PARTNER_OWNER' || orgRole === 'PARTNER_LEAD' || orgRole === 'CISO_RESELLER';
  // Billing (view plans, subscribe, pay invoices, Stripe portal for card/invoices; cancellation is handled by the CS team, not self-serve) is
  // org-manager only — mirrors the backend assertOrgManager() gate on /api/billing/*.
  const { canManageBilling, canUseRedTeamPlanning } = getOrgPermissions();

  // Keep context name in sync when it changes (e.g. after entering/exiting client view)
  useEffect(() => {
    const sync = () => setClientContextName(localStorage.getItem('partnerContextOrgName') || '');
    window.addEventListener('storage', sync);
    sync();
    return () => window.removeEventListener('storage', sync);
  }, [location]);

  // Show "AML Program" only when the org is enrolled in at least one AML framework. An org whose
  // every enrolled framework is an AML one gets the AML Program as its home — the GRC dashboard
  // is noise for them. amlOnly stays null while deciding so the "/" redirect waits instead of
  // flashing the wrong home. Enrollment comes from /audit/compliance-summary (one row per
  // ENROLLED framework) — /compliance/frameworks returns the whole catalog including frameworks
  // the org never adopted, which made this check never pass. Rows are matched by framework NAME,
  // the house rule (ids differ per org/env).
  const [hasAml, setHasAml] = useState(false);
  const [amlOnly, setAmlOnly] = useState(null);
  // Names of the frameworks the org is enrolled in; drives framework-specific workspaces (frameworkModules.js).
  const [enrolledNames, setEnrolledNames] = useState([]);
  useEffect(() => {
    if (isCiso || isCpa) { setAmlOnly(false); setEnrolledNames([]); return; }
    let alive = true;
    setAmlOnly(null);
    Promise.all([
      resolveAmlFrameworks().catch(() => []),
      getComplianceSummary().catch(() => []),
    ]).then(([amlList, enrolled]) => {
      if (!alive) return;
      setEnrolledNames(enrolled.map((f) => f.frameworkName).filter(Boolean));
      setHasAml(amlList.length > 0);
      const amlNames = new Set(amlList.flatMap((f) => [f.displayName, f.shortName].filter(Boolean)));
      setAmlOnly(amlList.length > 0 && enrolled.length > 0
        && enrolled.every((f) => amlNames.has(f.frameworkName)));
    });
    return () => { alive = false; };
  }, [isCiso, isCpa, clientContextName]);

  const cpaNav      = isCpaAdmin ? [...NAV_CPA, NAV_CPA_LIBRARY] : NAV_CPA;
  // An AML-only org gets an AML-shaped menu: sections/items flagged grcOnly disappear, the
  // "Dashboard" item points at the AML Program, and a section can carry an amlLabel when its
  // remaining items deserve a better name. Direct URLs keep working — this trims the menu,
  // not permissions.
  const standardNav = NAV_STANDARD
    .filter(section => !(amlOnly && section.grcOnly))
    .map(section => ({
      ...section,
      label: amlOnly && section.amlLabel ? section.amlLabel : section.label,
      items: section.items
        .filter(item => (hasAml || !item.requiresAml) && (canUseRedTeamPlanning || !item.requiresOrgManager)
          && !(amlOnly && item.grcOnly))
        .map(item => (amlOnly && item.to === '/dashboard/userHome' ? { ...item, to: '/dashboard/aml' } : item)),
    }))
    .filter(section => section.items.length > 0);
  // Framework workspaces (e.g. ISMS for ISO 27001) appear right after the Compliance section.
  const moduleSections = modulesForFrameworks(enrolledNames);
  if (moduleSections.length > 0) {
    const at = standardNav.findIndex(sec => sec.label === 'Compliance');
    standardNav.splice(at >= 0 ? at + 1 : standardNav.length, 0, ...moduleSections);
  }
  const navSections = isCiso ? NAV_CISO : isCpa ? cpaNav : standardNav;
  const defaultHome = isCiso ? '/dashboard/reseller' : isCpa ? '/dashboard/cpa'
    : amlOnly ? '/dashboard/aml' : '/dashboard/userHome';
  // Still deciding whether this is an AML-only org — render nothing for a beat rather than
  // bouncing the user to the GRC home and then away from it.
  const homeElement = (!isCiso && !isCpa && amlOnly === null) ? null : <Navigate to={defaultHome} />;

  const defaultOpen = new Set([
    'Overview', 'Assets & Vendors', 'Testing', 'Compliance', 'ISMS', 'Attack Surface & Testing',
    'Reseller Portal', 'CPA Portal', 'Client Management',
    'Workspace', 'Clients', 'Lead Pipeline', 'Firm Settings', 'Library',
  ]);
  const [openSections, setOpenSections] = useState(defaultOpen);
  const toggleSection = (label) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(label) ? next.delete(label) : next.add(label);
    return next;
  });

  useEffect(() => {
    const stored = localStorage.getItem('userName');
    if (stored) localStorage.setItem('username', stored);
  }, []);

  // Poll demo status from server so every user in the org sees the banner
  useEffect(() => {
    const checkDemo = () => {
      import('../api').then(({ default: axiosInstance }) => {
        axiosInstance.get('/demo/status')
          .then(r => setIsDemo(r.data?.isDemo === true))
          .catch(() => {});
      });
    };
    // Immediate update when seed/reset fires in the same tab (no need to wait for poll)
    const handleDemoChanged = (e) => setIsDemo(e.detail?.isDemo === true);
    window.addEventListener('demoStatusChanged', handleDemoChanged);
    checkDemo();
    const interval = setInterval(checkDemo, 60000); // re-check every 60s
    return () => {
      clearInterval(interval);
      window.removeEventListener('demoStatusChanged', handleDemoChanged);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('orgRole');
    clearTestOrg();
    navigate('/login');
  };

  const getPageTitle = () => {
    const p = location.pathname;
    if (p.includes('/meta-library'))           return t("nav.Meta Library", "Meta Library");
    if (p.includes('/userHome'))               return t("nav.Dashboard", "Dashboard");
    if (p.includes('/isms/roles'))             return t("nav.Roles & Owner", "Roles & Owner");
    if (p.includes('/isms/risks'))             return t("nav.Risk Register", "Risk Register");
    if (p.includes('/isms/soa'))               return t("nav.Statement of Applicability", "Statement of Applicability");
    if (p.includes('/isms'))                   return t("nav.ISMS", "ISMS");
    if (p.includes('/ai-governance'))          return t("nav.AI Governance", "AI Governance");
    if (p.includes('/aml'))                    return t("nav.AML Program", "AML Program");
    if (p.includes('/vendors'))                return t("nav.Vendors", "Vendors");
    if (p.includes('/assets'))                 return t("nav.Assets", "Assets");
    if (p.includes('/scope'))                  return t("nav.Scope & Boundary", "Scope & Boundary");
    if (p.includes('/engagements'))            return t("nav.Service Onboarding", "Service Onboarding");
    if (p.includes('/tests'))                  return t("nav.Tests", "Tests");
    if (p.includes('/settings'))               return t("nav.Settings", "Settings");
    if (p.includes('/support'))                return t('MyTickets.title', 'My Support Tickets');
    if (p.includes('/okf'))                    return t("nav.AI Context Export (OKF)", "AI Context Export (OKF)");
    if (p.includes('/compliance/frameworks'))  return t("nav.Frameworks", "Frameworks");
    if (p.includes('/compliance/controls'))    return t("nav.Controls", "Controls");
    if (p.includes('/compliance/policies'))    return t("nav.Policies", "Policies");
    if (p.includes('/people'))               return t("nav.People Directory", "People Directory");
    if (p.includes('/trust-center'))         return t("nav.Trust Center", "Trust Center");
    if (p.includes('/access-reviews'))       return t("nav.Access Reviews", "Access Reviews");
    if (p.includes('/audit-hub'))            return t("nav.Auditor Portal", "Auditor Portal");
    if (p.includes('/training'))             return t("nav.Training", "Training");
    if (p.includes('/my-tasks'))             return t("nav.My Tasks", "My Tasks");
    if (p.includes('/compliance/documents'))   return t("nav.Documents", "Documents");
    if (p.includes('/compliance/audits'))      return t("nav.Audits", "Audits");
    if (p.includes('/evidence'))              return t("nav.Evidence", "Evidence");
    if (p.includes('/org-documents'))         return t("nav.Document Editor", "Document Editor");
    if (p.includes('/iasm'))                  return t("nav.Internal ASM & BAS", "Internal ASM & BAS");
    if (p.includes('/asm'))                   return t("nav.External ASM", "External ASM");
    if (p.includes('/red-team'))              return t("nav.Red Team Planning", "Red Team Planning");
    if (p.includes('/reseller'))              return t("nav.Reseller Portal", "Reseller Portal");
    if (p.includes('/cpa-findings'))          return t("nav.Findings Inbox", "Findings Inbox");
    if (p.includes('/cpa'))                   return t("nav.Command Center", "Command Center");
    if (p.includes('/billing'))               return t("nav.Billing & Subscriptions", "Billing & Subscriptions");
    if (p.includes('/compliance/hub'))        return t("nav.Compliance Hub", "Compliance Hub");
    if (p.includes('/reports'))               return t("nav.Executive Reports", "Executive Reports");
    if (p.includes('/integrations'))          return t("nav.Evidence Automation", "Evidence Automation");
    if (p.includes('/marketplace'))           return t("nav.Partner Marketplace", "Partner Marketplace");
    if (p.includes('/partner'))               return t("nav.Partner Portal", "Partner Portal");
    if (p.includes('/clients'))               return t("nav.My Clients", "My Clients");
    if (p.includes('/client-view'))           return localStorage.getItem('partnerContextOrgName') || 'Client View';
    return t("nav.Dashboard", "Dashboard");
  };

  const initials = username.slice(0, 2).toUpperCase();

  return (
    <div className={`db-layout ${collapsed ? 'db-collapsed' : ''}`}>
      {/* ── Sidebar ─────────────────────────────────── */}
      <aside className="db-sidebar">
        {/* Brand */}
        <div className="db-brand">
          <div className="db-brand-logo">
            <i className="fas fa-shield-alt" />
          </div>
          <div className="db-brand-text">
            <span className="db-brand-name">CATAAM</span>
            <span className="db-brand-sub">
              {isCiso ? 'Reseller Portal' : isCpa ? 'CPA Portal' : 'Security Platform'}
            </span>
          </div>
          <button className="db-collapse-btn" onClick={() => setCollapsed(c => !c)} title={collapsed ? 'Expand' : 'Collapse'}>
            <i className={`fas fa-chevron-${collapsed ? 'right' : 'left'}`} />
          </button>
        </div>

        {/* Client context banner */}
        {(isCiso || isCpa) && clientContextName && !collapsed && (
          <div style={{ margin: '0 12px 4px', background: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <i className="fas fa-eye" style={{ marginRight: 4 }} />Client View
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#4c1d95', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {clientContextName}
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('partnerContextOrgId');
                localStorage.removeItem('partnerContextOrgName');
                setClientContextName('');
                navigate('/dashboard/clients');
              }}
              style={{ fontSize: 11, background: '#ede9fe', color: '#6d28d9', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontWeight: 600 }}>
              Exit
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="db-nav">
          {navSections.map(section => {
            const isOpen = openSections.has(section.label);
            return (
              <div key={section.label} className="db-section">
                <button
                  className="db-section-label"
                  onClick={() => !collapsed && toggleSection(section.label)}
                  title={collapsed ? section.label : undefined}
                >
                  <span className="db-section-label-text">{t('nav.' + section.label, section.label)}</span>
                  {!collapsed && (
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} db-section-chevron`} />
                  )}
                </button>
                {(isOpen || collapsed) && section.items.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) => `db-item${isActive ? ' db-item-active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    <i className={`fas ${item.icon} db-item-icon`} />
                    <span className="db-item-label">{t('nav.' + item.label, item.label)}</span>
                    {item.isNew && !collapsed && (
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 9999, background: '#dbeafe', color: '#1d4ed8' }}>
                        {t('nav.New', 'NEW')}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}

          {/* Account section — billing hidden for CPA, partner portal shown for partners */}
          {!isCpa && (
            <div className="db-section">
              <div className="db-section-label">{t('nav.Account', 'Account')}</div>
              {canManageBilling && (
                <NavLink to="/dashboard/billing"
                  className={({ isActive }) => `db-item${isActive ? ' db-item-active' : ''}`}
                  title={collapsed ? t('nav.Billing', 'Billing') : undefined}>
                  <i className="fas fa-credit-card db-item-icon" />
                  <span className="db-item-label">{t('nav.Billing', 'Billing')}</span>
                </NavLink>
              )}
              {isPartner && (
                <NavLink to="/dashboard/partner"
                  className={({ isActive }) => `db-item${isActive ? ' db-item-active' : ''}`}
                  title={collapsed ? t('nav.Partner Portal', 'Partner Portal') : undefined}>
                  <i className="fas fa-handshake db-item-icon" />
                  <span className="db-item-label">{t('nav.Partner Portal', 'Partner Portal')}</span>
                </NavLink>
              )}
            </div>
          )}

          <div className="db-section db-section-settings">
            <NavLink
              to="/dashboard/settings"
              className={({ isActive }) => `db-item${isActive ? ' db-item-active' : ''}`}
              title={collapsed ? 'Settings' : undefined}
            >
              <i className="fas fa-sliders-h db-item-icon" />
              <span className="db-item-label">{t('nav.Settings', 'Settings')}</span>
            </NavLink>
          </div>
        </nav>

        {/* Footer: user + logout */}
        <div className="db-sidebar-footer">
          {!collapsed && (
            <div style={{ marginBottom: 10 }}>
              <LanguageSwitcher />
            </div>
          )}
          <div className="db-user">
            <div className="db-user-avatar">{initials}</div>
            <div className="db-user-info">
              <div className="db-user-name">{username}</div>
              {org && <div className="db-user-org">{org}</div>}
            </div>
          </div>
          <button className="db-logout-btn" onClick={handleLogout} title="Sign out">
            <i className="fas fa-sign-out-alt" />
          </button>
        </div>
      </aside>

      {/* ── Main area ───────────────────────────────── */}
      <div className="db-main">
        {/* Top bar */}
        <header className="db-topbar">
          <h1 className="db-topbar-title">{getPageTitle()}</h1>
          <button className="db-help-btn" onClick={() => setShowSupport(true)}>
            <i className="fas fa-life-ring" /> {t('RaiseTicketModal.needHelp', 'Need help?')}
          </button>
        </header>
        {showSupport && <RaiseTicketModal onClose={() => setShowSupport(false)} />}

        {/* Demo mode banner — visible to all users in the org */}
        {isDemo && (
          <div className="db-demo-banner">
            <i className="fas fa-flask" />
            <span><strong>Demo Mode Active</strong> — All data in this organization is simulated. Do not connect real integrations until demo mode is exited.</span>
            <button className="db-demo-exit" onClick={() => navigate('/dashboard/settings', { state: { tab: 'demo' } })}>
              Exit Demo Mode <i className="fas fa-arrow-right" />
            </button>
          </div>
        )}

        {/* Test-org banner — this tenant is exempt from every payment wall, and the tag has no
            expiry, so label it plainly rather than letting it pass for a paying customer. */}
        {isTestOrg() && (
          <div className="db-testorg-banner">
            <i className="fas fa-vial" />
            <span>
              <strong>Test Organization</strong> — billing is bypassed for this tenant: subscriptions,
              plan limits and iASM credits are not enforced. Not a billable customer.
            </span>
          </div>
        )}

        {/* Content */}
        <main className="db-content">
          <MfaReminderBanner />
          <Routes>
            <Route path="/"                                              element={homeElement} />
            <Route path="/userHome"                                      element={<UserHome />} />
            <Route path="/meta-library"                                  element={isCpaAdmin ? <MetadataLibrary /> : <Navigate to="/dashboard/userHome" replace />} />
            <Route path="/vendors"                                       element={<Vendors />} />
            <Route path="/vendors/:vendorName"                           element={<VendorOverviewPage />} />
            <Route path="/vendors/cards"                                 element={<VendorCards />} />
            <Route path="/vendors/cards/:vendorName"                     element={<VendorDetailsPage />} />
            <Route path="/assets"                                        element={<Assets />} />
            <Route path="/scope"                                         element={<ScopeBoundaryPage />} />
            <Route path="/engagements"                                   element={<EngagementsPage />} />
            <Route path="/engagements/:id"                               element={<EngagementDetail />} />
            <Route path="/tests"                                         element={<Tests />} />
            <Route path="/tests/:id"                                     element={<TestDetails />} />
            <Route path="/settings"                                      element={<Settings />} />
            <Route path="/support"                                       element={<MyTickets />} />
            <Route path="/compliance/frameworks"                         element={<FrameworksPage />} />
            <Route path="/compliance/frameworks/:frameworkId/requirements" element={<RequirementsPage />} />
            <Route path="/compliance/documents"                          element={<DocumentsPage />} />
            <Route path="/compliance/controls"                           element={<ControlsPage />} />
            <Route path="/compliance/audits"                             element={<AuditPage />} />
            <Route path="/compliance/policies"                           element={<PoliciesPage />} />
            <Route path="/people"                                        element={<PeoplePage />} />
            <Route path="/trust-center"                                  element={<TrustCenterSettings />} />
            <Route path="/access-reviews"                                element={<AccessReviewPage />} />
            <Route path="/audit-hub"                                     element={<AuditHub />} />
            <Route path="/training"                                      element={<TrainingPage />} />
            <Route path="/my-tasks"                                      element={<MyTasksPage />} />
            <Route path="/evidence"                                      element={<EvidencePage />} />
            <Route path="/org-documents/:id"                            element={<OrgDocumentEditor />} />
            <Route path="/document-details"                              element={<DocumentDetails />} />
            <Route path="/billing"                                       element={canManageBilling ? <BillingPage /> : <Navigate to="/dashboard/userHome" replace />} />
            <Route path="/compliance/hub"                                element={<ComplianceHub />} />
            <Route path="/ai-governance"                                 element={<AiGovOverview />} />
            <Route path="/ai-governance/assets"                          element={<AiAssetInventory />} />
            <Route path="/isms"                                          element={<IsmsOverview />} />
            <Route path="/isms/roles"                                    element={<IsmsRoles />} />
            <Route path="/isms/risks"                                    element={<RiskRegister />} />
            <Route path="/isms/soa"                                      element={<IsmsSoa />} />
            <Route path="/ai-governance/soa"                             element={<StatementOfApplicability />} />
            <Route path="/ai-governance/risk-register"                   element={<AiRiskRegister />} />
            <Route path="/ai-governance/documents"                       element={<AiGovDocuments />} />
            <Route path="/ai-governance/monitoring"                      element={<AiCcmMonitoring />} />
            <Route path="/aml"                                           element={<AmlOverview />} />
            <Route path="/aml/overview"                                  element={<Navigate to="/dashboard/aml" replace />} />
            <Route path="/aml/risk-assessment"                           element={<AmlRiskAssessment />} />
            <Route path="/aml/segments"                                  element={<AmlCustomerSegments />} />
            <Route path="/aml/filings"                                   element={<AmlFilings />} />
            <Route path="/aml/vendors"                                   element={<AmlVendorDueDiligence />} />
            <Route path="/reports"                                       element={<ExecutiveReports />} />
            {/* Evidence Automation retired — it duplicated the real Vendors→Tests vendor-evidence
                engine (which runs GitHub branch-protection, AWS MFA, etc. for real against connected
                vendors and maps them to controls). Redirect old links to Vendors. */}
            <Route path="/integrations"                                  element={<Navigate to="/dashboard/vendors" replace />} />
            <Route path="/okf"                                           element={<OkfContextExportSettings />} />
            <Route path="/marketplace"                                   element={<MarketplaceDirectory />} />
            <Route path="/partner"                                       element={<PartnerMarketplace />} />
            <Route path="/clients"                                       element={<MyClients />} />
            <Route path="/client-view"                                   element={<ClientView />} />
            <Route path="/cpa-findings"                                  element={<CpaFindingsInbox />} />
            <Route path="/partner-billing"                               element={<PartnerBilling />} />
            <Route path="/partner-stripe"                               element={<PartnerStripeConnect />} />
            <Route path="/partner-leads"                                element={<PartnerLeads />} />
            <Route path="/partner-branding"                             element={<PartnerBrandingSettings />} />
            <Route path="/asm"                                           element={<AsmDashboard />} />
            <Route path="/iasm"                                          element={<InternalAsmDashboard />} />
            <Route path="/red-team-planning"                             element={<OrgRedTeamPlanning />} />
            <Route path="/red-team"                                      element={<PartnerRedTeam />} />
            <Route path="/reseller"                                      element={<ResellerDashboard />} />
            <Route path="/cpa"                                           element={<CpaDashboard />} />
            <Route path="*"                                              element={homeElement} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

export default Dashboard;
